/**
 * The sync engine: what the device carries down, what it pushes back, and how
 * it finds out which of the two actually happened.
 *
 * Three moves, in this order, and the order matters:
 *
 *  1. **push** what is queued here. Local work goes first so the server has
 *     seen it before anything comes back down — otherwise a pull can overwrite
 *     a field the tech edited two minutes ago and the edit is gone with no
 *     error message anywhere.
 *  2. **pull** everything after the cursor, applied field by field.
 *  3. **hydrate** the working set, but only when there isn't one, or when it
 *     is old enough to be the wrong day's work.
 *
 * The cursor is a server sequence number, not a timestamp: a device resuming
 * from `seq` sees every change after it exactly once, which a timestamp cursor
 * cannot promise across concurrent transactions.
 */

import { ApiError } from "./api";
import * as outbox from "./outbox";
import {
  type Entity,
  ENTITIES,
  get,
  type Row,
  inventory,
  patch,
  readMeta,
  remove,
  replaceAll,
  writeMeta,
} from "./store";

const CURSOR = "cursor";
const HYDRATED_AT = "hydrated_at";

/** A working set older than this is the wrong day's work. */
export const STALE_AFTER_MS = 12 * 60 * 60 * 1000;

/** How many push failures before the engine stops trying on its own. */
const GIVE_UP_AFTER = 3;

export type SyncResult = {
  pushed: number;
  refused: number;
  pulled: number;
  hydrated: number;
  cursor: number;
  /** Written for a person. The status surface shows it as-is. */
  message: string;
  /** True when the engine could not reach the server at all. */
  offline: boolean;
};

type PushResponse = {
  applied: number;
  rejected: number;
  superseded: number;
  clamped: number;
  cursor: number;
  projected: Record<string, number>;
  details: {
    entity: string;
    /** The id this device minted, so a refusal can be matched to its change. */
    entity_id: string;
    field: string | null;
    reason: string;
    /** "merge" — the server would not accept it. "project" — it accepted it
     *  and could not write it, which is the one that used to be silent (D15). */
    stage: "merge" | "project";
  }[];
};

type PullResponse = {
  changes: {
    seq: number;
    entity: string;
    entity_id: string;
    op: "create" | "update" | "delete";
    field: string | null;
    value: unknown;
    server_ts: string;
  }[];
  cursor: number;
  has_more: boolean;
};

type WorkingSetResponse = {
  cursor: number;
  horizon_hours: number;
  generated_at: string | null;
  apply_order: string[];
  counts: Record<string, number>;
  total: number;
  warnings: string[];
  entities: Record<string, Row[]>;
};

/** Injected so the tests drive this without a network. */
export type Transport = {
  push: (body: ReturnType<typeof outbox.envelope>) => Promise<PushResponse>;
  pull: (since: number) => Promise<PullResponse>;
  workingSet: () => Promise<WorkingSetResponse>;
};

/* ------------------------------------------------------------------ push */

/**
 * Send the queue and reconcile the answer.
 *
 * The reconciliation is the whole point. `applied` counts what the merge
 * accepted; `projected` counts what became rows; `details` names every
 * difference. A change can pass the first and fail the second — a job line
 * pointing at a job that was never created — and treating a 200 as success
 * loses that work with nothing on screen to say so (D15).
 */
export async function push(transport: Transport): Promise<{ pushed: number; refused: number; cursor: number }> {
  const queue = await outbox.pending();
  if (queue.length === 0) return { pushed: 0, refused: 0, cursor: await cursor() };

  const response = await transport.push(outbox.envelope(queue));

  // Matched by entity_id, which is the id this device minted — so a refusal
  // lands on the exact change that caused it and not on its neighbours. The
  // server reports the field too, because one record can have several queued
  // field updates and only one of them refused.
  const reasonFor = new Map(
    response.details.map((d) => [`${d.entity_id}::${d.field ?? ""}`, d.reason] as const),
  );
  // A refusal with no field (a create, a delete) condemns the whole record:
  // the row does not exist, so the field updates queued against it cannot
  // land either, and reporting only the create would leave the rest looking
  // accepted.
  const condemned = new Set(
    response.details.filter((d) => d.field === null).map((d) => d.entity_id),
  );

  const accepted: number[] = [];
  const refused: { seq: number; reason: string }[] = [];

  for (const change of queue) {
    if (change.seq === undefined) continue;
    const reason =
      reasonFor.get(`${change.entity_id}::${change.field ?? ""}`) ??
      (condemned.has(change.entity_id)
        ? (reasonFor.get(`${change.entity_id}::`) ?? "the record this change belongs to was refused")
        : undefined);

    if (reason === undefined) accepted.push(change.seq);
    else refused.push({ seq: change.seq, reason });
  }

  await outbox.markSent(accepted);
  for (const { seq, reason } of refused) await outbox.markRejected([seq], reason);
  await undoRefusedInstalls(queue, new Set(refused.map((r) => r.seq)));
  await dropRefusedCreates(queue, new Set(refused.map((r) => r.seq)));
  await outbox.forgetSent();

  // The cursor is NOT moved up to the push response's. That number is the end of
  // the log as the push left it, which is past everything other people changed
  // since this device last pulled, and past everything the server worked out
  // from this very push — a price, a number, a derived status. Jumping there
  // skipped all of it, and it was never delivered. The pull that follows asks
  // the server to leave out this device's own changes instead (D81).
  return { pushed: accepted.length, refused: refused.length, cursor: await cursor() };
}

/* ------------------------------------------------------------------ pull */

/**
 * Apply everything the server has recorded since the cursor.
 *
 * Field by field, in sequence order, which is where the server's merge already
 * settled who wins — so this does not re-decide anything, it just lands the
 * result. Pages until `has_more` is false: a device back from a week off has
 * more than one page of other people's work waiting.
 */
export async function pull(transport: Transport, { maxPages = 50 } = {}): Promise<number> {
  let applied = 0;
  let since = await cursor();

  for (let page = 0; page < maxPages; page += 1) {
    const response = await transport.pull(since);
    if (response.changes.length === 0) {
      if (response.cursor > since) await writeMeta(CURSOR, response.cursor);
      break;
    }

    for (const change of response.changes) {
      if (!isKnownEntity(change.entity)) continue; // an entity this client build does not carry
      const entity = change.entity as Entity;

      if (change.op === "delete") {
        await remove(entity, change.entity_id, change.server_ts);
      } else if (change.op === "create") {
        const fields = (change.value ?? {}) as Record<string, unknown>;
        await patch(entity, change.entity_id, { ...fields, ...settled(entity) });
      } else if (change.field) {
        await patch(entity, change.entity_id, { [change.field]: change.value, ...settled(entity) });
      }
      applied += 1;
    }

    since = response.cursor;
    await writeMeta(CURSOR, since);
    if (!response.has_more) break;
  }

  return applied;
}

/**
 * The optimistic flags this row no longer needs, now that the server's own
 * version of it has arrived.
 *
 * `pending_install` is a local-only column: `actions.commission` sets it so a
 * scan made with no signal shows as done on the worklist straight away. It
 * used to be cleared only by the next working set, because the server's reply
 * had nowhere to travel. Since SYNC-6 the server logs what it derives and the
 * pull after every push carries it, so the arrival of any change for this
 * asset is the signal that the real values are here and the stand-in can go.
 *
 * Written as one named rule rather than a special case inside the loop,
 * because the next local-only flag will want the same treatment.
 */
function settled(entity: Entity): Record<string, unknown> {
  return entity === "asset" ? { pending_install: false } : {};
}

/**
 * Take back the serial a refused offline scan put on the phone's copy.
 *
 * `actions.commission` shows a scanned serial on the asset straight away, as
 * a local patch, so the worklist shrinks while the tech is still on the
 * ladder. If the server refuses that scan, the patch has to go: otherwise,
 * once the refusal is dismissed, the phone shows a serial the register does
 * not have, and checks new scans against it (D47).
 */
/**
 * A row the server refused to create does not exist, so the device stops
 * showing it. It used to stay as a blank $0.00 line on the job — through
 * reloads and navigation — looking like work that had landed (SYNC-9). What it
 * was, and why it was refused, is kept with the refusal in the outbox and named
 * in the banner, which is where somebody can act on it.
 */
async function dropRefusedCreates(queue: outbox.Queued[], refused: Set<number>): Promise<void> {
  for (const change of queue) {
    if (change.seq === undefined || !refused.has(change.seq) || change.op !== "create") continue;
    if (change.entity === "asset_event") continue; // undone field by field above
    await remove(change.entity, change.entity_id, new Date().toISOString());
  }
}

async function undoRefusedInstalls(queue: outbox.Queued[], refused: Set<number>): Promise<void> {
  for (const change of queue) {
    if (change.seq === undefined || !refused.has(change.seq)) continue;
    if (change.entity !== "asset_event" || change.op !== "create") continue;
    const value = (change.value ?? {}) as Record<string, unknown>;
    if (value.kind !== "installed" || typeof value.asset_id !== "string") continue;
    const scanned = (value.payload as Record<string, unknown> | undefined)?.serial;
    const row = await get<Row>("asset", value.asset_id);
    // Only if it still holds *this* scan — a later, successful one stays.
    if (row && row.serial === scanned) {
      await patch("asset", value.asset_id, { serial: null, pending_install: false });
    }
  }
}

/* --------------------------------------------------------------- hydrate */

/**
 * Take a fresh working set: everything this tech needs to work with no signal.
 *
 * Replaces rather than merges, and in one transaction, because a device
 * carrying half of one day and half of another has nothing on screen that says
 * which is which (see `store.replaceAll`).
 *
 * Refuses to run while there is queued work. Hydrating clears the entity
 * stores, so a create still sitting in the outbox would vanish from the screen
 * while remaining in the queue — the app would show one thing and send another.
 */
export async function hydrate(transport: Transport): Promise<number> {
  const queued = await outbox.pending();
  if (queued.length > 0) {
    throw new SyncError(
      `${queued.length} change${queued.length > 1 ? "s" : ""} still to send — they go up before a new working set comes down.`,
    );
  }

  const response = await transport.workingSet();

  const payload: Partial<Record<Entity, Row[]>> = {};
  for (const [name, rows] of Object.entries(response.entities)) {
    if (isKnownEntity(name)) payload[name as Entity] = rows;
  }

  await replaceAll(payload);
  await writeMeta(CURSOR, response.cursor);
  await writeMeta(HYDRATED_AT, new Date().toISOString());

  return response.total;
}

/* ------------------------------------------------------------ the whole thing */

export class SyncError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SyncError";
  }
}

/**
 * Push, pull, and hydrate if the set is missing or stale.
 *
 * Never throws on a network failure. A field app that pops an error because a
 * van went through a tunnel teaches people to dismiss errors, and the queue is
 * doing exactly what it exists for in that moment.
 */
export async function syncNow(transport: Transport, { force = false } = {}): Promise<SyncResult> {
  const before = await outbox.counts();

  try {
    const sent = await push(transport);
    const needsSet = force || (await isStale());
    const hydrated = needsSet && sent.refused === 0 ? await hydrateQuietly(transport) : 0;
    const pulled = hydrated > 0 ? 0 : await pull(transport);

    return {
      pushed: sent.pushed,
      refused: sent.refused,
      pulled,
      hydrated,
      cursor: await cursor(),
      offline: false,
      message: describe(sent.pushed, sent.refused, pulled, hydrated),
    };
  } catch (cause) {
    const unreachable = cause instanceof ApiError && cause.status === 0;
    await writeMeta("last_error", unreachable ? null : String(cause));

    return {
      pushed: 0,
      refused: 0,
      pulled: 0,
      hydrated: 0,
      cursor: await cursor(),
      offline: unreachable,
      message: unreachable
        ? before.pending > 0
          ? `No connection. ${before.pending} change${before.pending > 1 ? "s" : ""} waiting to send.`
          : "No connection."
        : cause instanceof Error
          ? cause.message
          : "Sync failed.",
    };
  }
}

/** A hydrate that a queued change quietly cancels rather than failing the sync. */
async function hydrateQuietly(transport: Transport): Promise<number> {
  try {
    return await hydrate(transport);
  } catch (cause) {
    if (cause instanceof SyncError) return 0;
    throw cause;
  }
}

function describe(pushed: number, refused: number, pulled: number, hydrated: number): string {
  const parts: string[] = [];
  if (pushed > 0) parts.push(`sent ${pushed}`);
  if (pulled > 0) parts.push(`received ${pulled}`);
  if (hydrated > 0) parts.push(`carrying ${hydrated} records`);
  if (refused > 0) parts.push(`${refused} refused`);
  return parts.length === 0 ? "Up to date." : `${parts.join(", ")}.`;
}

/* ----------------------------------------------------------------- state */

export function cursor(): Promise<number> {
  return readMeta<number>(CURSOR, 0);
}

export function hydratedAt(): Promise<string | null> {
  return readMeta<string | null>(HYDRATED_AT, null);
}

export async function isStale(): Promise<boolean> {
  const at = await hydratedAt();
  if (at === null) return true;
  return Date.now() - Date.parse(at) > STALE_AFTER_MS;
}

/** Everything the status surface needs, in one call. */
export async function status() {
  const [queue, carrying, at, position] = await Promise.all([
    outbox.counts(),
    inventory(),
    hydratedAt(),
    cursor(),
  ]);
  return {
    pending: queue.pending,
    refused: queue.rejected,
    carrying: Object.values(carrying).reduce((a, b) => a + b, 0),
    byEntity: carrying,
    hydratedAt: at,
    cursor: position,
    stale: at === null || Date.now() - Date.parse(at) > STALE_AFTER_MS,
  };
}

function isKnownEntity(name: string): name is Entity {
  return (ENTITIES as readonly string[]).includes(name);
}

export { GIVE_UP_AFTER };
